// src/components/training/summary/SummaryRow.tsx
import React from "react";
import { SummaryDTO, TimePercent } from "../../../types/SummaryDTO";
import {
  formatDDMM,
  formatHMM,
  formatTimePercentCompact,
  normalizeIsoDate,
  sortKeysWithOrder,
  splitWeekKey,
  STAT_COLORS,
  STATS_ORDER,
  weekRangeFromWeekStart,
} from "./summaryFormat";

type Props = {
  summary: SummaryDTO;
  showHrZones: boolean;

  hrZoneOrder: string[];
  domainOrder: string[];
};

function renderLines(
  map: Record<string, TimePercent> | undefined | null,
  order: string[] | null,
  colorMap?: Record<string, string>,
  labelWidthPx: number = 110,
  requiredKeys?: string[]
) {
  const safeMap = map ?? {};

  const baseKeys = requiredKeys
    ? [...requiredKeys, ...Object.keys(safeMap).filter((k) => !requiredKeys.includes(k))]
    : Object.keys(safeMap);

  const keys = order ? sortKeysWithOrder(baseKeys, order) : baseKeys.sort();
  if (keys.length === 0) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
      {keys.map((k) => {
        const color = colorMap?.[k] ?? "#333";
        const tp = safeMap[k] ?? { timeSeconds: 0, percent: 0 };

        return (
          <div
            key={k}
            style={{
              display: "grid",
              gridTemplateColumns: `${labelWidthPx}px auto`,
              columnGap: "8px",
              alignItems: "baseline",
              color,
              fontFamily: "ui-monospace, Consolas, monospace",
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ fontWeight: 700 }}>{k}:</span>
            <span>{formatTimePercentCompact(tp)}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function SummaryRow({ summary, showHrZones, hrZoneOrder, domainOrder }: Props) {
  const { year, week } = splitWeekKey(summary.key);

  const weekStartIso = normalizeIsoDate((summary as any).startDate);
  const { start, end } = weekRangeFromWeekStart(weekStartIso);

  return (
    <tr>
      <td>
        <div style={{ lineHeight: 1.1 }}>
          <div style={{ fontSize: "1.4rem", fontWeight: 800 }}>{week}</div>
          <div style={{ fontSize: "0.9rem", fontWeight: 600, opacity: 0.75 }}>{year}</div>
          <div style={{ fontSize: "0.85rem", opacity: 0.75 }}>
            {formatDDMM(start)} – {formatDDMM(end)}
          </div>
        </div>
      </td>

      <td style={{ fontFamily: "ui-monospace, Consolas, monospace" }}>
        {formatHMM(summary.totalTimeSeconds)}
      </td>

      <td>
        {summary.totalDistance.toFixed(2)} km
        <div>{Math.round(summary.totalElevationGain)} m</div>
      </td>

      <td>{renderLines(summary.domains, domainOrder, STAT_COLORS, 92)}</td>

      <td style={{ minWidth: showHrZones ? 180 : 260 }}>
        {showHrZones
          ? renderLines(summary.hrZones, hrZoneOrder, undefined, 32)
          : renderLines(summary.stats, [...STATS_ORDER], STAT_COLORS, 110, [...STATS_ORDER])}
      </td>
    </tr>
  );
}